import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { ssoSignInStart } from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.actions";
import * as authSelectors from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.selectors";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { StringParam } from "serialize-query-params";
import { Loading, TextStyle } from "../../components";
import { StyledAuthBody, StyledAuthContent, StyledAuthFrame, StyledAuthLogo, StyledAuthMain, StyledAuthMedia, StyledAuthMediaWrap, } from "./Auth.styled";
import { useNavigateAfterSuccess } from "./Auth.utils";
/**
 * Where the assertion callback sends the browser. Swaps the single-use code for a session and
 * gets out of the way — there is nothing for the person to do here, so it renders a loader and
 * hands any failure back to the sign-in page, which is where they can retry.
 */
var SsoCallback = function () {
    var queryParams = useQueryParams({
        code: StringParam,
    })[0];
    var code = queryParams.code;
    // Router
    var navigate = useNavigate();
    var navigateAfterSuccess = useNavigateAfterSuccess();
    // Redux
    var dispatch = useDispatch();
    var hasAccessToken = useSelector(authSelectors.hasAccessToken);
    var ssoSignInError = useSelector(authSelectors.getSsoSignInError);
    var user = authHooks.useSelf().user;
    // An error already in the store when this screen mounts belongs to an earlier attempt; only
    // a failure of this exchange may send the person back.
    var initialSsoSignInErrorRef = React.useRef(ssoSignInError);
    // The code is single-use and the API burns it on the first attempt, so a re-render must not
    // send it twice — the second exchange would fail and overwrite a sign-in that succeeded.
    var hasExchangedRef = React.useRef(false);
    // Back to the shared sign-in page rather than an office-specific one: the page keeps the
    // address that was typed, so the person picks up where they left off without the URL
    // carrying a customer's name.
    //
    // SECURITY: `error` must be one of the fixed codes passed below, never a value taken from
    // the URL or the API. It lands in the address bar and the browser history, which is no
    // place for provider or API text, let alone a personal identifier.
    var returnToSignIn = React.useCallback(function (error) {
        navigate("/auth/sso?error=".concat(encodeURIComponent(error)), {
            replace: true,
        });
    }, [navigate]);
    // Lifecycle
    React.useEffect(function () {
        if (hasExchangedRef.current) {
            return;
        }
        hasExchangedRef.current = true;
        if (!code) {
            returnToSignIn("invalid_request");
            return;
        }
        dispatch(ssoSignInStart.getAction({
            code: code,
            onFailure: function () {
                returnToSignIn("rejected");
            },
        }));
    }, [code, dispatch, returnToSignIn]);
    // Covers a failure after the exchange itself succeeded (storing the tokens), which
    // onFailure never hears about; without it the loader would spin forever.
    React.useEffect(function () {
        if (!ssoSignInError ||
            ssoSignInError === initialSsoSignInErrorRef.current) {
            return;
        }
        returnToSignIn("rejected");
    }, [returnToSignIn, ssoSignInError]);
    React.useEffect(function () {
        if (!hasAccessToken || !user) {
            return;
        }
        navigateAfterSuccess();
    }, [hasAccessToken, navigateAfterSuccess, user]);
    // Render
    return (_jsxs(StyledAuthFrame, { children: [_jsx(StyledAuthContent, { children: _jsxs(StyledAuthMain, { children: [_jsx(StyledAuthLogo, {}), _jsxs(StyledAuthBody, { children: [_jsx(Loading, {}), _jsx(TextStyle, { variation: "subdued", children: getLocalizedText("auth.sso.signing_in") })] })] }) }), _jsx(StyledAuthMediaWrap, { children: _jsx(StyledAuthMedia, {}) })] }));
};
// eslint-disable-next-line import/no-default-export
export default SsoCallback;
